import { ApiError, authFetch, authFetchJson } from '../api';
import type { Chart, GameMode } from '@shared/chart';
import type { AnyReplayInputs, ReplayOutcome } from '@shared/replay';
import type { RunTimingReport } from '@shared/antiCheat/runTiming';

export interface MapSummary {
  id: string;
  slug: string;
  /**
   * Which gameplay system this map targets. The same `slug` can exist
   * in both modes (`on-and-on` Pulse + `on-and-on` Circles), so callers
   * that mix-and-match (e.g. profile screens) must always pair slug
   * with mode for any lookup or display.
   */
  mode: GameMode;
  title: string;
  description: string;
  bpm: number;
  difficulty: number;
  totalBeats: number;
  palette: Record<string, string>;
  isBuiltin: boolean;
  isPublic: boolean;
  authorUid: string;
  playCount: number;
  likeCount: number;
  liked: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MapDetail extends MapSummary {
  chart: Chart;
  leaderboard: LeaderboardEntry[];
}

export interface LeaderboardEntry {
  runId: string;
  userUid: string;
  displayName: string | null;
  isAnonymous: boolean;
  score: number;
  accuracy: number;
  perfects: number;
  goods: number;
  misses: number;
  serverValidated: boolean;
  createdAt: string;
}

export interface ListMapsResponse {
  total: number;
  maps: MapSummary[];
}

export async function fetchMapList(opts: {
  sort?: 'likes' | 'recent' | 'difficulty' | 'plays';
  search?: string;
  difficulty?: number;
  limit?: number;
  offset?: number;
  includeBuiltin?: boolean;
  /**
   * Filter by game mode. Omit to query Pulse (the legacy default — see
   * the server route's matching default in maps.ts).
   */
  mode?: GameMode;
} = {}): Promise<ListMapsResponse> {
  const params = new URLSearchParams();
  if (opts.sort) params.set('sort', opts.sort);
  if (opts.search) params.set('search', opts.search);
  if (opts.difficulty) params.set('difficulty', String(opts.difficulty));
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.offset) params.set('offset', String(opts.offset));
  if (opts.includeBuiltin === false) params.set('includeBuiltin', '0');
  if (opts.mode) params.set('mode', opts.mode);
  return authFetchJson<ListMapsResponse>(`/api/maps?${params.toString()}`);
}

export async function fetchMapDetail(slug: string, opts: { mode?: GameMode } = {}): Promise<MapDetail> {
  const qs = opts.mode ? `?mode=${encodeURIComponent(opts.mode)}` : '';
  return authFetchJson<MapDetail>(`/api/maps/${encodeURIComponent(slug)}${qs}`);
}

/**
 * The signed-in player's own maps across every mode — including private
 * drafts that never appear in the public browse lists. Backs the "My
 * maps" library on the profile page and the "edit an existing map"
 * picker in the builders.
 */
export async function fetchMyMaps(): Promise<MapSummary[]> {
  const res = await authFetchJson<{ maps: MapSummary[] }>(`/api/maps/mine`);
  return res.maps;
}

export async function createMap(chart: Chart, isPublic: boolean): Promise<{ id: string; slug: string }> {
  return authFetchJson<{ id: string; slug: string }>(`/api/maps`, {
    method: 'POST',
    json: { chart, isPublic },
  });
}

export async function updateMap(
  id: string,
  patch: { chart?: Chart; isPublic?: boolean },
): Promise<{ id: string; slug: string; isPublic: boolean }> {
  return authFetchJson(`/api/maps/${id}`, { method: 'PATCH', json: patch });
}

export async function deleteMap(id: string): Promise<void> {
  const res = await authFetch(`/api/maps/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
}

export async function likeMap(id: string): Promise<{ liked: boolean; count: number }> {
  return authFetchJson(`/api/maps/${id}/like`, { method: 'POST' });
}

export async function unlikeMap(id: string): Promise<{ liked: boolean; count: number }> {
  return authFetchJson(`/api/maps/${id}/like`, { method: 'DELETE' });
}

export async function recordPlay(id: string): Promise<void> {
  const res = await authFetch(`/api/maps/${id}/play`, { method: 'POST' });
  if (!res.ok) {
    throw new Error(`Record play failed: ${res.status}`);
  }
}

export interface SubmitRunResponse {
  runId: string | null;
  score: number;
  serverValidated: boolean;
  skipped?: string;
  /** Best-ever progression after server-side GREATEST() upsert; null if nothing was raised. */
  progression?: { stars: number; cups: number; competitiveEligible: boolean } | null;
  /**
   * False when the server rejected the progression upsert (e.g. transient
   * DB error). The client should warn the player so they know their
   * stars/cups didn't actually save. Always true on the happy path.
   */
  progressionSaved?: boolean;
}

const SUBMIT_RUN_MAX_ATTEMPTS = 3;
const SUBMIT_RUN_RETRY_DELAY_MS = 500;

/**
 * Decides whether a submitRun failure is worth retrying. We retry on
 * obvious "transient" cases (network blip, server 5xx) but not on 4xx -
 * a 4xx tells us the request itself is wrong and a retry will just fail
 * the same way.
 */
function isTransientSubmitError(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.status >= 500;
  }
  // fetch() throws TypeError on network failure, DNS failure, or aborted
  // connections - all worth a second attempt.
  return err instanceof TypeError;
}

/**
 * Open a server-authoritative run session: the server stamps the start
 * time into a signed token the client returns on submit, letting the
 * server measure real elapsed time against its own (un-fakeable) clock.
 * Best-effort — returns `null` when the server is unreachable (offline
 * play) or the request fails; the run still submits, just without the
 * server-clock proof, so it saves as practice without trophies or ranking.
 */
export async function startRun(mapId: string): Promise<string | null> {
  const res = await authFetchJson<{ token: string }>(`/api/runs/start`, {
    method: 'POST',
    json: { mapId },
  });
  return res.token ?? null;
}

export async function submitRun(input: {
  mapId: string;
  outcome: ReplayOutcome;
  /**
   * Captured player inputs for this run. The shape is mode-specific —
   * Pulse submits flip-down/up events, Circles submits (beat, x, y) taps —
   * and both wrap the per-loop input batches in `replay.loops` so the
   * server can re-simulate every loop the player attempted. Server
   * dispatches validation on the chart's mode discriminator.
   */
  replay?: AnyReplayInputs;
  /** Stars/cups reached this run. Server raises the best-ever values; never lowers. */
  progression?: { stars: number; cups: number };
  /**
   * Anti-cheat timing stream (`(beat, wallMs, monoMs)` samples +
   * synthetic-input tally). Lets the server verify the run progressed in
   * real time. Optional — omitted for replay-preview runs and tolerated
   * for older clients, whose submissions save as practice.
   */
  timing?: RunTimingReport | null;
  /**
   * Server-issued run token from {@link startRun}, proving when the run
   * began against the server clock. Optional — absent when offline / on
   * older clients.
   */
  runToken?: string | null;
}): Promise<SubmitRunResponse> {
  // Run submission is the most player-impactful endpoint - losing a good
  // run to a network blip feels awful. Retry transient failures with a
  // small fixed backoff. Duplicate runs are benign (the leaderboard takes
  // the best score per player), so retrying is safe even if the server
  // succeeded but the response was lost.
  let lastErr: unknown;
  for (let attempt = 1; attempt <= SUBMIT_RUN_MAX_ATTEMPTS; attempt += 1) {
    try {
      return await authFetchJson<SubmitRunResponse>(`/api/runs`, {
        method: 'POST',
        json: input,
      });
    } catch (err) {
      lastErr = err;
      if (attempt === SUBMIT_RUN_MAX_ATTEMPTS || !isTransientSubmitError(err)) {
        throw err;
      }
      console.warn(
        `submitRun attempt ${attempt}/${SUBMIT_RUN_MAX_ATTEMPTS} failed, retrying:`,
        err,
      );
      await new Promise((resolve) =>
        setTimeout(resolve, SUBMIT_RUN_RETRY_DELAY_MS * attempt),
      );
    }
  }
  throw lastErr;
}

export interface LeaderboardResponse {
  mapId: string;
  total: number;
  entries: LeaderboardEntry[];
}

export async function fetchLeaderboard(mapId: string, limit = 25): Promise<LeaderboardResponse> {
  return authFetchJson(`/api/runs/leaderboard/${mapId}?limit=${limit}`);
}

export interface PersonalBest {
  score: number;
  accuracy: number;
  perfects: number;
  goods: number;
  misses: number;
  completed: boolean;
  createdAt: string;
}

export async function fetchPersonalBest(mapId: string): Promise<PersonalBest | null> {
  const res = await authFetchJson<{ run: PersonalBest | null }>(
    `/api/runs/personal-best/${mapId}`,
  );
  return res.run ?? null;
}
